import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  gamesDB, sessionsDB, aiAnalysesDB, preGameIntentsDB,
  miniBlocksDB, userSettingsDB, rankSnapshotsDB,
} from '../lib/supabase';
import { useAuth } from './AuthContext';
import { log } from '../lib/logger';

/**
 * APEX v8.16 — DataContext
 *
 * Centralise toutes les données utilisateur chargées depuis Supabase :
 *   - games, sessions, aiAnalyses, miniBlocks
 *   - pendingIntent, allIntents
 *   - userSettings, rankSnapshots
 *   - loading (chargement initial en cours)
 *
 * Le chargement se déclenche automatiquement quand `user` change (via useAuth).
 * En cas d'erreur partielle, on continue avec les autres sources (Promise.allSettled).
 *
 * Expose aussi les setters pour permettre les mutations optimistes
 * (le composant met à jour le state immédiatement, puis appelle Supabase).
 */

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const { user } = useAuth();

  const [games, setGames] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [miniBlocks, setMiniBlocks] = useState([]);
  const [pendingIntent, setPendingIntent] = useState(null);
  const [allIntents, setAllIntents] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [rankSnapshots, setRankSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setGames([]);
      setSessions([]);
      setAiAnalyses([]);
      setMiniBlocks([]);
      setPendingIntent(null);
      setUserSettings(null);
      setRankSnapshots([]);
      setAllIntents([]);
      return;
    }
    setLoading(true);
    // v7.7 : allSettled — si une source échoue (ex: colonne DB manquante),
    // les autres se chargent quand même. Évite l'écran vide sur erreur partielle.
    Promise.allSettled([
      gamesDB.list(),
      sessionsDB.list(),
      aiAnalysesDB.list(),
      miniBlocksDB.list(),
      preGameIntentsDB.getUnmatched(),
      userSettingsDB.get(),
      rankSnapshotsDB.list(),
      preGameIntentsDB.list(),
    ]).then((results) => {
      const [g, s, a, m, i, settings, ranks, allI] = results;
      const errors = [];

      if (g.status === 'fulfilled') {
        setGames(g.value);
        log.info(`[load] ${Array.isArray(g.value) ? g.value.length : 0} games chargées depuis la DB`);
      } else { errors.push('games'); log.error('[load] games ÉCHEC:', g.reason); }

      if (s.status === 'fulfilled') setSessions(s.value);
      else { errors.push('sessions'); log.error('[load] sessions:', s.reason); }

      if (a.status === 'fulfilled') setAiAnalyses(a.value);
      else log.error('[load] aiAnalyses:', a.reason);

      if (m.status === 'fulfilled') {
        // v7.8.9 : filtre défensif — exclut les anciennes missions "Importe
        // ta première game" qui pourraient rester en base (spam objectifs).
        const cleaned = (m.value || []).filter(b =>
          b?.title !== 'Importe ta première game' &&
          b?.data?.mission_type !== 'welcome' &&
          b?.data?.label !== 'Importe ta première game'
        );
        setMiniBlocks(cleaned);
      } else { errors.push('objectifs'); log.error('[load] miniBlocks:', m.reason); }

      if (i.status === 'fulfilled') setPendingIntent(i.value);
      else log.error('[load] intents:', i.reason);

      if (settings.status === 'fulfilled') setUserSettings(settings.value);
      else { errors.push('réglages'); log.error('[load] settings:', settings.reason); }

      if (ranks.status === 'fulfilled') setRankSnapshots(ranks.value);
      else log.error('[load] ranks:', ranks.reason);

      if (allI.status === 'fulfilled') setAllIntents(allI.value);
      else log.error('[load] allIntents:', allI.reason);

      if (errors.length > 0) {
        log.warn(`[load] Échecs partiels : ${errors.join(', ')}. L'app continue avec les données disponibles.`);
      }
    }).finally(() => setLoading(false));
  }, [user]);

  const value = {
    // States
    games, sessions, aiAnalyses, miniBlocks,
    pendingIntent, allIntents,
    userSettings, rankSnapshots,
    loading,
    // Setters (pour mutations optimistes)
    setGames, setSessions, setAiAnalyses, setMiniBlocks,
    setPendingIntent, setAllIntents,
    setUserSettings, setRankSnapshots,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (ctx === null) {
    throw new Error('useData must be used within a DataProvider');
  }
  return ctx;
}
